// src/pages/directorate/DirectorateofTechnical.js
import { motion } from "framer-motion";

const DirectorateofTechnical = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center">
        <img
          src="/images/tvetbb.jpeg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center text-white px-6">
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-3"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Directorate for Technical and Vocational Education and Training
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Empowering Futures Through Skills
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-justify text-gray-600 leading-relaxed">
            The Directorate for Technical and Vocational Education and Training (TVET) at 
            the Federal Polytechnic Bida was created to tackle youth unemployment and a 
            lack of skilled workers in Nigeria.<p> </p> Seeing the need for practical, hands-on
            skills in the country, the polytechnic leadership established this directorate. Its mission is to move beyond just theory and focus on teaching real-world skills that employers actually need.
            The directorate was built with two main units to achieve this goal:<p></p>
            The Nigerian Skills Qualification Framework (NSQF) Unit:
            <p>1. This unit offers standardized, nationally recognized certifications. It doesn't matter 
            where you learned a skill on the job, as an apprentice, or in a classroom—the NSQF 
            tests and certifies your competency, allowing you to progress to higher levels.<p> </p>
            2. The Vocational Program Unit: This unit provides short, intensive training courses (4-6 months) 
            in various trades. It is designed for quick skills acquisition, allowing youths to 
            rapidly learn a vocation, become empowered, and either find employment or start their 
            own businesses.<p>  </p>
            Since its founding, the TVET Directorate has been a key player in providing quality skill 
            development for the community and Niger State, empowering individuals with the certificates 
            and confidence to build successful careers.</p>
            
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <img
              src="/images/jamesmkdp.jpg"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              The Director Speech
            </h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              A big welcome to all our students, partners, and staff.
              My message is simple: skills pay the bills. This directorate exists for 
              one reason: to give you the practical training you need to succeed in 
              the real world.<p></p>
              Whether you are here for the long-term NSQF program to get your national 
              certificate, or for a short vocational course to learn a trade quickly, 
              you are in the right place.<p></p>
              Your journey here will end with a certificate in your hand and the confidence
              to use your skill to make a living. You won't just be looking for a job; you 
              will be the skilled professional that employers are looking for.<p></p>
              We are here to guide you. Your future starts now.<p></p>
              Let's get to work.
              <p><b>The Director,</b></p>
              <p><b>Directorate for Technical and Vocational Education and Training (TVET).</b></p>
            </p>
            <p className="mt-4 text-blue-700 font-semibold">Engr. Jame Baba Mokwa</p>
            <p className="text-gray-500">The Director</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-justify text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To be the leading centre of excellence for practical skills development and 
              innovation, producing a generation of certified, job-ready professionals and 
              entrepreneurs for a dynamic global economy.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To provide accessible, high-quality Technical and Vocational Education and 
              Training (TVET) through the Nigerian Skills Qualification Framework (NSQF) 
              and short-term vocational programs. We are committed to equipping youths and 
              professionals with relevant, practical skills, fostering innovation, and promoting 
              lifelong learning to reduce unemployment and drive sustainable national 
              development.
            </p>
          </div>
        </motion.section>

        {/* Services Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Services</h2>
          <ul className="grid md:grid-cols-2 gap-6 text-gray-600 list-disc pl-6">
            <li>Technical skills courses (e.g., welding, ICT, agriculture).</li>
            <li>Entrepreneurship Guidance (business skills training).</li>
            <li>Industry Apprenticeships (hands-on work experience).</li>
            <li>Career Counseling (vocational pathway advice).</li>
          </ul>
        </motion.section>

        {/* Activities Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Activities</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Offering national skills certification (NSQF).",
                img: "/images/tvet1.jpeg",
              },
              {
                title: "Providing short-term vocational training (4-6 months).",
                img: "/images/tvet2.jpeg",
              },
              {
                title: "Teaching practical, hands-on job skills",
                img: "/images/tvet3.jpeg",
              },
            ].map((activity, i) => (
              <div
                key={i}
                className="bg-gray-50 rounded-lg shadow hover:shadow-lg transition p-4"
              >
                <img
                  src={activity.img}
                  alt={activity.title}
                  className="w-full h-40 object-cover rounded-md mb-4"
                />
                <h3 className="font-semibold text-gray-800">{activity.title}</h3>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["tvets.jpeg", "tvetg1.jpeg", "tvetg2.jpeg", "tvetg3.jpeg"].map(
              (img, i) => (
                <img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md hover:scale-105 transition-transform"
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Directorate
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> The Federal Polytechnic, Bida, Niger State,
            Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> tvet@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default DirectorateofTechnical;
